import React, { useCallback, useRef, useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  MessageSquare,
  Music,
  FileText,
  Play,
  Pause,
} from "lucide-react";
import Button from "@/components/ui/Button";
import { CanvasCommentPin } from "../canvas-comment-pin";
import { CanvasCommentsDrawer } from "../canvas-comments-drawer";
import MediaControls from "./media-controls";
import {
  ChromeIcon,
  ChromeAvatar,
  ChromeGradient,
} from "@/components/ad-chrome/chrome-elements";
import { ExpandableText, expansionLayout } from "@/components/ad-chrome/expandable-text";
import { PhoneFrame, PHONE_FRAME_REACH } from "@/components/ad-chrome/phone-frame";

// Canvas preview max render dimensions (px) — canvas scales to fit within these
const CANVAS_MAX_WIDTH = 800;
const CANVAS_MAX_HEIGHT = 900;

interface MediaOverride {
  url: string;
  file: File;
}

interface MediaSlot {
  canvas_component_info_id: string;
  url?: string | null;
  original_name?: string | null;
  slot_index: number;
  type?: string;
}

interface CanvasComment {
  id: string;
  x_position: string;
  y_position: string;
  is_resolved: boolean;
  replies_count: number;
  interface_id: string;
}

interface AdBuilderData {
  canvas: {
    canvas_master?: {
      canvas_info_aggregated?: {
        canvasWidth: number;
        canvasHeight: number;
        selectedInterface: string;
      };
    };
    components: Array<{
      id: string;
      canvas_component_master_name: string;
      canvas_component_sub_master_name: string;
      canvas_component_sub_master_display_name: string;
      locations: Record<
        string,
        {
          interface_id: string;
          location_json: { x: number; y: number; width: number; height: number; z_index: number };
        }
      >;
      aggregated_json?: any;
    }>;
  };
}

interface AssetVersion {
  id: string;
  media_json?: Record<string, any> | null;
}

interface AdBuilderCanvasProps {
  adBuilderData: AdBuilderData | null;
  activeDevice: string;
  setActiveDevice: (device: string) => void;
  showFullScreen: boolean;
  setShowFullScreen: (value: boolean) => void;
  commentModeActive: boolean;
  setCommentModeActive: (value: boolean | ((v: boolean) => boolean)) => void;
  comments: CanvasComment[];
  activeCommentId: string | null;
  pendingPin: { x: number; y: number } | null;
  isDrawerOpen: boolean;
  activeVersionId: string | null;
  activeInterfaceIdRef: React.MutableRefObject<string>;
  isSaving: boolean;
  contentBySubMaster: Record<string, string>;
  mediaOverrides: Record<string, MediaOverride>;
  mediaSlots: MediaSlot[];
  assetVersions: AssetVersion[];
  carouselSlideIndex: Record<string, number>;
  setCarouselSlideIndex: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  audioPlaying: Record<string, boolean>;
  setAudioPlaying: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  audioRefs: React.MutableRefObject<Record<string, HTMLAudioElement | null>>;
  activeTrack: Record<string, number>;
  setActiveTrack: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  openDrawer: (commentId: string | null) => void;
  closeDrawer: () => void;
  setPendingPin: (pin: { x: number; y: number } | null) => void;
  onSaveChanges: () => void;
  /**
   * Public preview: the ad only. Hides commenting and saving and never enters
   * comment mode; full-screen viewing stays available.
   */
  readOnly?: boolean;
}

const AdBuilderCanvas: React.FC<AdBuilderCanvasProps> = ({
  adBuilderData,
  activeDevice,
  setActiveDevice,
  showFullScreen,
  setShowFullScreen,
  commentModeActive,
  setCommentModeActive,
  comments,
  activeCommentId,
  pendingPin,
  isDrawerOpen,
  activeVersionId,
  activeInterfaceIdRef,
  isSaving,
  contentBySubMaster,
  mediaOverrides,
  mediaSlots,
  assetVersions,
  carouselSlideIndex,
  setCarouselSlideIndex,
  audioPlaying,
  setAudioPlaying,
  audioRefs,
  activeTrack,
  setActiveTrack,
  openDrawer,
  closeDrawer,
  setPendingPin,
  onSaveChanges,
  readOnly = false,
}) => {
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});
  const [videoFullscreen, setVideoFullscreen] = useState<Record<string, boolean>>({});
  // "… more" texts the reviewer has expanded, and how much extra height each
  // needs; the rest of the canvas is pushed down by that much.
  const [expandedTexts, setExpandedTexts] = useState<Record<string, boolean>>({});
  const [textExtras, setTextExtras] = useState<Record<string, number>>({});
  const measureText = useCallback((id: string, extra: number) => {
    setTextExtras((prev) => (prev[id] === extra ? prev : { ...prev, [id]: extra }));
  }, []);

  // The fullscreen scale is derived from the viewport, so a resize has to
  // re-render for the canvas to refit. Only listens while fullscreen is open.
  const [, forceResizeRender] = useState(0);
  useEffect(() => {
    if (!showFullScreen) return;
    const onResize = () => forceResizeRender((n) => n + 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [showFullScreen]);

  // Escape is the expected way out of a fullscreen overlay, and without it the
  // only exit is the toolbar button.
  useEffect(() => {
    if (!showFullScreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowFullScreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showFullScreen, setShowFullScreen]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setShowFullScreen(!showFullScreen)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          {showFullScreen ? (
            <Minimize2 size={16} />
          ) : (
            <Maximize2 size={16} />
          )}
          <span className="text-sm font-medium">
            {showFullScreen ? "Exit Full Screen" : "View Full Screen"}
          </span>
        </button>

        {!readOnly && (
        <div className="flex">
          {/* Comments toggle */}
          <Button
            onClick={() => setCommentModeActive((v) => !v)}
            type="secondary"
            leadingIcon={<MessageSquare size={15} />}
            className="mr-4"
          >
            {commentModeActive ? "Exit Comments" : "Comments"}
            {comments.length > 0 && (
              <span
                className="ml-1 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center"
                style={{
                  backgroundColor: commentModeActive
                    ? "#EA33C0"
                    : "rgba(0,0,0,0.3)",
                  color: "black",
                }}
              >
                {comments.length}
              </span>
            )}
          </Button>
          <Button
            type="primary"
            onClick={onSaveChanges}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
        )}
      </div>

      {/* Canvas Preview */}
      {(() => {
        if (!adBuilderData) return null;

        const canvasMaster = adBuilderData.canvas.canvas_master;
        const canvasInfo = canvasMaster?.canvas_info_aggregated;

        if (!canvasInfo) {
          return (
            <div className="text-sm text-gray-500">
              No canvas data available.
            </div>
          );
        }

        const canvasWidth: number = canvasInfo.canvasWidth;
        const canvasHeight: number = canvasInfo.canvasHeight;
        const selectedInterface: string = canvasInfo.selectedInterface;

        if (!canvasWidth || !canvasHeight) {
          return (
            <div className="text-sm text-gray-500">
              Canvas dimensions unavailable.
            </div>
          );
        }

        if (!selectedInterface) {
          return (
            <div className="text-sm text-gray-500">
              No interface selected.
            </div>
          );
        }

        const deviceSet = new Set<string>();
        adBuilderData.canvas.components.forEach((comp) => {
          Object.keys(comp.locations).forEach((key) => deviceSet.add(key));
        });

        if (deviceSet.size === 0) {
          return (
            <div className="text-sm text-gray-500">
              No device layouts found.
            </div>
          );
        }

        const currentDevice = activeDevice || Array.from(deviceSet)[0];
        const isMobile = currentDevice === "mobile";

        // Fullscreen gets its own budget from the viewport, minus the toolbar
        // and padding. Without this the scale stayed pinned to the inline
        // maximums, so "View Full Screen" only dimmed the background and left
        // the canvas exactly the same size.
        const maxW = showFullScreen
          ? Math.max(320, window.innerWidth - 96)
          : CANVAS_MAX_WIDTH;
        const maxH = showFullScreen
          ? Math.max(320, window.innerHeight - 160)
          : CANVAS_MAX_HEIGHT;

        // Never scales above 1:1 in the inline view, matching the previous
        // behaviour; fullscreen is allowed to enlarge a small mobile canvas,
        // which is the point of opening it.
        const scale = Math.min(maxW / canvasWidth, maxH / canvasHeight);
        const renderWidth = canvasWidth * scale;
        const renderHeight = canvasHeight * scale;

        const visibleComponents = adBuilderData.canvas.components
          .filter((comp) => {
            const loc = comp.locations[currentDevice];
            if (!loc) return false;
            if (loc.interface_id !== selectedInterface) return false;
            // "Show in template only" means the element belongs to the design
            // template but is not part of the rendered ad, so the preview
            // omits it.
            if (comp.aggregated_json?.properties?.showInTemplateOnly)
              return false;
            return true;
          })
          .sort((a, b) => {
            const zA =
              a.locations[currentDevice]?.location_json?.z_index ?? 0;
            const zB =
              b.locations[currentDevice]?.location_json?.z_index ?? 0;
            return zA - zB;
          });

        // Track the active interface ID so the drawer can use it without static maps
        activeInterfaceIdRef.current = selectedInterface;

        // Filter comments that belong to the current interface (derived from canvas data)
        const canvasComments = comments.filter(
          (c) => c.interface_id === selectedInterface,
        );

        const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
          if (readOnly || !commentModeActive) return;
          const rect = e.currentTarget.getBoundingClientRect();
          const clickX = (e.clientX - rect.left) / scale;
          const clickY = (e.clientY - rect.top) / scale;
          setPendingPin({ x: clickX, y: clickY });
          openDrawer(null);
        };

        const canvasContent = (
          <div
            ref={canvasContainerRef}
            // A phone keeps its hardware frame visible outside the screen, so
            // clipping moves to the screen layer below.
            className={`relative bg-white ${isMobile ? "" : "overflow-hidden"}`}
            style={{
              width: renderWidth,
              height: renderHeight,
              cursor: commentModeActive ? "crosshair" : "default",
            }}
            onClick={handleCanvasClick}
          >
            <div
              className={isMobile ? "overflow-hidden rounded-[47px]" : undefined}
              style={{
                width: canvasWidth,
                height: canvasHeight,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            >
              {(() => {
                // Expanded texts push what is below them down, like a feed;
                // elements marked "Fixed on screen" stay put above the scroll.
                const { shifts, totalExtra } = expansionLayout(
                  visibleComponents.map((c) => {
                    const l = c.locations[currentDevice].location_json;
                    return {
                      id: c.id,
                      x: l.x,
                      y: l.y,
                      width: l.width,
                      height: l.height,
                      pinned: Boolean(c.aggregated_json?.properties?.pinned),
                      container: ["shape", "gradient"].includes(c.canvas_component_master_name),
                    };
                  }),
                  textExtras,
                );
                const isPinned = (c: any) => Boolean(c.aggregated_json?.properties?.pinned);

                const renderComponent = (comp: any) => {
                const loc = comp.locations[currentDevice];
                const { x, y: baseY, width, height: baseHeight, z_index } = loc.location_json;
                const y = baseY + (shifts[comp.id]?.dy ?? 0);
                const height = baseHeight + (shifts[comp.id]?.dh ?? 0);
                const masterName = comp.canvas_component_master_name;
                const subMasterName = comp.canvas_component_sub_master_name;
                const aggregated = comp.aggregated_json;
                const textSettings = aggregated?.textSettings;

                // Live text override: if this component's sub_master_name matches
                // a content field name, use the live value
                const liveText = contentBySubMaster[subMasterName];
                const displayText =
                  liveText !== undefined
                    ? liveText
                    : (aggregated?.content ?? aggregated?.label ?? "");

                // Live media override. Single-slot elements are keyed by the
                // component id alone, but slots that carry an explicit index
                // (the avatar, for one) are keyed `<id>_<slotIndex>` — so both
                // forms are checked, otherwise an uploaded profile picture
                // never appears on the canvas.
                const mediaOverride =
                  mediaOverrides[comp.id] ?? mediaOverrides[`${comp.id}_0`];
                // Also check slot url from API (already-uploaded media)
                const slotFromApi = mediaSlots.find(
                  (s) => s.canvas_component_info_id === comp.id,
                );
                // Check media_json from active asset version (has signed URL after fetchAssetVersions)
                const activeVersion = assetVersions.find(
                  (v) => v.id === activeVersionId,
                );
                const mediaJsonUrl =
                  ((activeVersion?.media_json?.[comp.id] ??
                    activeVersion?.media_json?.[`${comp.id}_0`]) as any)?.url ??
                  null;
                // Check aggregated_json media (for image/video components with uploaded media)
                const aggregatedMediaUrl =
                  aggregated?.media?.[0]?.url ?? aggregated?.media?.["0"]?.url ?? null;
                const mediaUrl =
                  mediaOverride?.url ??
                  slotFromApi?.url ??
                  mediaJsonUrl ??
                  aggregatedMediaUrl ??
                  aggregated?.content ??
                  null;

                return (
                  <div
                    key={comp.id}
                    style={{
                      position: "absolute",
                      left: x,
                      top: y,
                      width,
                      height,
                      // Fixed chrome stays above content scrolling under it.
                      zIndex: isPinned(comp) ? 1000 + z_index : z_index,
                    }}
                  >
                    {masterName === "image" ? (
                      (() => {
                        const isCarousel = subMasterName === "carousel";
                        // Gather all slots for this component (carousel has multiple)
                        const carouselSlots = isCarousel
                          ? mediaSlots.filter(
                              (s) =>
                                s.canvas_component_info_id === comp.id,
                            )
                          : null;
                        const slideIdx = carouselSlideIndex[comp.id] ?? 0;
                        const activeSlot = isCarousel
                          ? // Matched on the slot's own index rather than its
                            // position in the array: uploads arrive only for
                            // filled slots, so positional lookup showed media
                            // on the wrong slide whenever a slot was skipped.
                            carouselSlots?.find(
                              (s) => s.slot_index === slideIdx,
                            )
                          : null;
                        const activeOverride = isCarousel
                          ? mediaOverrides[`${comp.id}_${slideIdx}`]
                          : mediaOverride;
                        // Saved carousel media lives under a per-slot key in
                        // media_json (`<componentId>_<slotIndex>`). Reading only
                        // the slot list meant an upload disappeared after a
                        // reload, because the signed URL arrives on the asset
                        // version rather than on the slot record.
                        const carouselMediaJsonUrl = isCarousel
                          ? ((
                              activeVersion?.media_json?.[
                                `${comp.id}_${slideIdx}`
                              ] as any
                            )?.url ?? null)
                          : null;
                        const activeUrl = isCarousel
                          ? (activeOverride?.url ??
                            activeSlot?.url ??
                            carouselMediaJsonUrl ??
                            null)
                          : mediaUrl;
                        // Slide count comes from the element's declared slots,
                        // not just the uploaded media: counting uploads alone
                        // left a carousel with no media at zero slides, so the
                        // nav never rendered and it could not be paged at all.
                        const totalSlides = isCarousel
                          ? Math.max(
                              aggregated?.slotCount ?? 0,
                              carouselSlots?.length ?? 0,
                              1,
                            )
                          : 1;

                        return (
                          <div
                            style={{ width: "100%", height: "100%" }}
                            className="relative"
                          >
                            {activeUrl ? (
                              <img
                                src={activeUrl}
                                alt={aggregated?.label ?? "image"}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  background:
                                    "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
                                }}
                                className="border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-1"
                              >
                                <svg
                                  className="w-7 h-7 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                                <span className="text-xs font-medium text-gray-400">
                                  {isCarousel
                                    ? `Card ${slideIdx + 1} / ${totalSlides}`
                                    : (aggregated?.label ?? "Image")}
                                </span>
                              </div>
                            )}
                            {/* Carousel nav */}
                            {isCarousel && totalSlides > 1 && (
                              <>
                                <button
                                  className="absolute left-1 top-1/2 -translate-y-1/2 w-5 h-5 bg-black/40 rounded-full flex items-center justify-center text-white hover:bg-black/60"
                                  style={{ zIndex: 10 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCarouselSlideIndex((prev) => ({
                                      ...prev,
                                      [comp.id]:
                                        (slideIdx - 1 + totalSlides) %
                                        totalSlides,
                                    }));
                                  }}
                                >
                                  <ChevronLeft size={12} />
                                </button>
                                <button
                                  className="absolute right-1 top-1/2 -translate-y-1/2 w-5 h-5 bg-black/40 rounded-full flex items-center justify-center text-white hover:bg-black/60"
                                  style={{ zIndex: 10 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCarouselSlideIndex((prev) => ({
                                      ...prev,
                                      [comp.id]: (slideIdx + 1) % totalSlides,
                                    }));
                                  }}
                                >
                                  <ChevronRight size={12} />
                                </button>
                                {/* Dots */}
                                <div
                                  className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1"
                                  style={{ zIndex: 10 }}
                                >
                                  {Array.from({ length: totalSlides }).map(
                                    (_, i) => (
                                      <button
                                        key={i}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setCarouselSlideIndex((prev) => ({
                                            ...prev,
                                            [comp.id]: i,
                                          }));
                                        }}
                                        className="w-1.5 h-1.5 rounded-full transition-colors"
                                        style={{
                                          backgroundColor:
                                            i === slideIdx
                                              ? "white"
                                              : "rgba(255,255,255,0.4)",
                                        }}
                                      />
                                    ),
                                  )}
                                </div>
                                {/* Counter */}
                                <div
                                  className="absolute top-1 right-1 bg-black/50 text-white text-[9px] px-1.5 py-0.5 rounded-full"
                                  style={{ zIndex: 10 }}
                                >
                                  {slideIdx + 1}/{totalSlides}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })()
                    ) : masterName === "video" ? (
                      (() => {
                        const isCarousel = subMasterName === "carousel";
                        const carouselSlots = isCarousel
                          ? mediaSlots.filter(
                              (s) =>
                                s.canvas_component_info_id === comp.id,
                            )
                          : null;
                        const slideIdx = carouselSlideIndex[comp.id] ?? 0;
                        const activeSlot = isCarousel
                          ? // Matched on the slot's own index rather than its
                            // position in the array: uploads arrive only for
                            // filled slots, so positional lookup showed media
                            // on the wrong slide whenever a slot was skipped.
                            carouselSlots?.find(
                              (s) => s.slot_index === slideIdx,
                            )
                          : null;
                        const activeOverride = isCarousel
                          ? mediaOverrides[`${comp.id}_${slideIdx}`]
                          : mediaOverride;
                        // Saved carousel media lives under a per-slot key in
                        // media_json (`<componentId>_<slotIndex>`). Reading only
                        // the slot list meant an upload disappeared after a
                        // reload, because the signed URL arrives on the asset
                        // version rather than on the slot record.
                        const carouselMediaJsonUrl = isCarousel
                          ? ((
                              activeVersion?.media_json?.[
                                `${comp.id}_${slideIdx}`
                              ] as any
                            )?.url ?? null)
                          : null;
                        const activeUrl = isCarousel
                          ? (activeOverride?.url ??
                            activeSlot?.url ??
                            carouselMediaJsonUrl ??
                            null)
                          : mediaUrl;
                        // Slide count comes from the element's declared slots,
                        // not just the uploaded media: counting uploads alone
                        // left a carousel with no media at zero slides, so the
                        // nav never rendered and it could not be paged at all.
                        const totalSlides = isCarousel
                          ? Math.max(
                              aggregated?.slotCount ?? 0,
                              carouselSlots?.length ?? 0,
                              1,
                            )
                          : 1;
                        const videoKey = isCarousel ? `${comp.id}_${slideIdx}` : comp.id;

                        return (
                          <div
                            style={{ width: "100%", height: "100%" }}
                            className="relative group"
                          >
                            {activeUrl ? (
                              <>
                                <video
                                  ref={(el) => {
                                    videoRefs.current[videoKey] = el;
                                  }}
                                  src={activeUrl}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                  muted
                                  loop
                                />
                                {/* Video controls appear on hover */}
                                <div className="absolute bottom-0 left-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <MediaControls
                                    mediaRef={videoRefs.current[videoKey]}
                                    type="video"
                                    onFullscreen={() => {
                                      const video = videoRefs.current[videoKey];
                                      if (!video) return;
                                      if (document.fullscreenElement) {
                                        document.exitFullscreen();
                                        setVideoFullscreen(prev => ({ ...prev, [videoKey]: false }));
                                      } else {
                                        video.requestFullscreen();
                                        setVideoFullscreen(prev => ({ ...prev, [videoKey]: true }));
                                      }
                                    }}
                                    isFullscreen={videoFullscreen[videoKey] ?? false}
                                    className="scale-[0.7] origin-bottom"
                                  />
                                </div>
                              </>
                            ) : (
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  background:
                                    "linear-gradient(135deg, #1f2937 0%, #374151 100%)",
                                }}
                                className="border-2 border-dashed border-gray-600 flex flex-col items-center justify-center gap-1"
                              >
                                <svg
                                  className="w-7 h-7 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <span className="text-xs font-medium text-gray-400">
                                  {isCarousel
                                    ? `Clip ${slideIdx + 1} / ${totalSlides}`
                                    : (aggregated?.label ?? "Video")}
                                </span>
                              </div>
                            )}
                            {isCarousel && totalSlides > 1 && (
                              <>
                                <button
                                  className="absolute left-1 top-1/2 -translate-y-1/2 w-5 h-5 bg-black/40 rounded-full flex items-center justify-center text-white hover:bg-black/60"
                                  style={{ zIndex: 10 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCarouselSlideIndex((prev) => ({
                                      ...prev,
                                      [comp.id]:
                                        (slideIdx - 1 + totalSlides) %
                                        totalSlides,
                                    }));
                                  }}
                                >
                                  <ChevronLeft size={12} />
                                </button>
                                <button
                                  className="absolute right-1 top-1/2 -translate-y-1/2 w-5 h-5 bg-black/40 rounded-full flex items-center justify-center text-white hover:bg-black/60"
                                  style={{ zIndex: 10 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCarouselSlideIndex((prev) => ({
                                      ...prev,
                                      [comp.id]: (slideIdx + 1) % totalSlides,
                                    }));
                                  }}
                                >
                                  <ChevronRight size={12} />
                                </button>
                                <div
                                  className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1"
                                  style={{ zIndex: 10 }}
                                >
                                  {Array.from({ length: totalSlides }).map(
                                    (_, i) => (
                                      <button
                                        key={i}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setCarouselSlideIndex((prev) => ({
                                            ...prev,
                                            [comp.id]: i,
                                          }));
                                        }}
                                        className="w-1.5 h-1.5 rounded-full transition-colors"
                                        style={{
                                          backgroundColor:
                                            i === slideIdx
                                              ? "white"
                                              : "rgba(255,255,255,0.4)",
                                        }}
                                      />
                                    ),
                                  )}
                                </div>
                                <div
                                  className="absolute top-1 right-1 bg-black/50 text-white text-[9px] px-1.5 py-0.5 rounded-full"
                                  style={{ zIndex: 10 }}
                                >
                                  {slideIdx + 1}/{totalSlides}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })()
                    ) : masterName === "audio" ? (
                      (() => {
                        const isPlaylist = subMasterName === "audio_playlist";
                        const audioSlots = mediaSlots.filter(
                          (s) => s.canvas_component_info_id === comp.id,
                        );
                        const currentTrack = activeTrack[comp.id] ?? 0;
                        const activeSlot =
                          audioSlots[currentTrack] ?? audioSlots[0];
                        const activeAudioOverride =
                          mediaOverrides[`${comp.id}_${currentTrack}`] ??
                          mediaOverride;
                        const activeAudioUrl =
                          activeAudioOverride?.url ?? activeSlot?.url ?? null;
                        const isPlaying = audioPlaying[comp.id] ?? false;

                        if (isPlaylist) {
                          return (
                            <div
                              style={{ width: "100%", height: "100%" }}
                              className="bg-gradient-to-br from-purple-50 to-pink-50 flex flex-col p-1 overflow-hidden"
                            >
                              <div className="text-[9px] font-semibold text-purple-500 mb-1 px-1">
                                Playlist
                              </div>
                              <div className="flex-1 overflow-y-auto space-y-0.5">
                                {(audioSlots.length > 0
                                  ? audioSlots
                                  : Array.from({ length: 3 })
                                ).map((slot: any, i: number) => (
                                  <button
                                    key={i}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveTrack((prev) => ({
                                        ...prev,
                                        [comp.id]: i,
                                      }));
                                      setAudioPlaying((prev) => ({
                                        ...prev,
                                        [comp.id]: true,
                                      }));
                                    }}
                                    className={`w-full flex items-center gap-1 px-1 py-0.5 rounded text-left transition-colors ${
                                      i === currentTrack
                                        ? "bg-purple-200"
                                        : "hover:bg-purple-100"
                                    }`}
                                  >
                                    <Music
                                      size={8}
                                      className={
                                        i === currentTrack
                                          ? "text-purple-600"
                                          : "text-gray-400"
                                      }
                                    />
                                    <span className="text-[8px] text-gray-600 truncate">
                                      {slot?.original_name ?? `Track ${i + 1}`}
                                    </span>
                                  </button>
                                ))}
                              </div>
                              {activeAudioUrl && (
                                <audio
                                  ref={(el) => {
                                    audioRefs.current[comp.id] = el;
                                  }}
                                  src={activeAudioUrl}
                                  onEnded={() =>
                                    setAudioPlaying((prev) => ({
                                      ...prev,
                                      [comp.id]: false,
                                    }))
                                  }
                                />
                              )}
                            </div>
                          );
                        }

                        // Single audio_box
                        return (
                          <div
                            style={{ width: "100%", height: "100%" }}
                            className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-dashed border-purple-200 flex flex-col items-center justify-center gap-1 group relative"
                          >
                            {activeAudioUrl ? (
                              <>
                                <audio
                                  ref={(el) => {
                                    audioRefs.current[comp.id] = el;
                                  }}
                                  src={activeAudioUrl}
                                  onEnded={() =>
                                    setAudioPlaying((prev) => ({
                                      ...prev,
                                      [comp.id]: false,
                                    }))
                                  }
                                />
                                {/* Simplified in-canvas controls */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const audio = audioRefs.current[comp.id];
                                    if (!audio) return;
                                    if (isPlaying) {
                                      audio.pause();
                                      setAudioPlaying((prev) => ({
                                        ...prev,
                                        [comp.id]: false,
                                      }));
                                    } else {
                                      audio.play();
                                      setAudioPlaying((prev) => ({
                                        ...prev,
                                        [comp.id]: true,
                                      }));
                                    }
                                  }}
                                  className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white hover:bg-purple-600"
                                >
                                  {isPlaying ? (
                                    <Pause size={14} />
                                  ) : (
                                    <Play size={14} />
                                  )}
                                </button>
                                <div className="w-4/5 h-1 bg-purple-200 rounded-full">
                                  <div className="h-full w-1/3 bg-purple-500 rounded-full" />
                                </div>
                                {/* Full controls appear on hover */}
                                <div className="absolute bottom-0 left-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <MediaControls
                                    mediaRef={audioRefs.current[comp.id]}
                                    type="audio"
                                    className="scale-[0.7] origin-bottom"
                                  />
                                </div>
                              </>
                            ) : (
                              <>
                                <Music size={20} className="text-purple-400" />
                                <span className="text-[9px] font-medium text-purple-400">
                                  {aggregated?.label ?? "Audio"}
                                </span>
                                <span className="text-[8px] text-purple-300">
                                  Upload audio →
                                </span>
                              </>
                            )}
                          </div>
                        );
                      })()
                    ) : masterName === "text" ? (
                      <ExpandableText
                        text={displayText}
                        softLimit={aggregated?.properties?.softLimit}
                        expanded={Boolean(expandedTexts[comp.id])}
                        onToggle={() =>
                          setExpandedTexts((prev) => ({ ...prev, [comp.id]: !prev[comp.id] }))
                        }
                        onMeasure={(extra) => measureText(comp.id, extra)}
                        boxHeight={baseHeight}
                        placeholder={`${comp.canvas_component_sub_master_display_name}…`}
                        style={{
                          fontSize: textSettings?.fontSize ?? 12,
                          fontWeight: textSettings?.fontWeight ?? "normal",
                          fontStyle: textSettings?.fontStyle ?? "normal",
                          textDecoration: textSettings?.textDecoration ?? "none",
                          fontFamily: textSettings?.fontFamily ?? "inherit",
                          textAlign: (textSettings?.textAlign as any) ?? "left",
                          lineHeight: textSettings?.lineHeight ?? 1.4,
                          letterSpacing:
                            textSettings?.letterSpacing != null
                              ? `${textSettings.letterSpacing}px`
                              : undefined,
                          textTransform: (textSettings?.textTransform as any) ?? undefined,
                          opacity:
                            textSettings?.opacity != null ? textSettings.opacity / 100 : 1,
                          color: displayText ? (textSettings?.color ?? "#000000") : "#9ca3af",
                        }}
                      />
                    ) : masterName === "shape" ? (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          backgroundColor:
                            aggregated?.shapeSettings?.fill ?? "#e5e7eb",
                          opacity: aggregated?.shapeSettings?.opacity ?? 1,
                          borderRadius:
                            subMasterName === "circle"
                              ? "50%"
                              : (aggregated?.shapeSettings?.borderRadius ?? 0),
                          border: aggregated?.shapeSettings?.borderWidth
                            ? `${aggregated.shapeSettings.borderWidth}px solid ${aggregated.shapeSettings.borderColor ?? "transparent"}`
                            : "none",
                        }}
                      />
                    ) : masterName === "button" ? (
                      (() => {
                        // Brand-aware defaults per sub-type
                        const brandDefaults: Record<
                          string,
                          {
                            bg: string;
                            color: string;
                            border?: string;
                            radius: string;
                            shadow?: string;
                          }
                        > = {
                          facebook: {
                            bg: "#FFFFFF",
                            color: "#1C2B33",
                            border: "1px solid #DADDE1",
                            radius: "6px",
                          },
                          instagram: {
                            bg: "#FFFFFF",
                            color: "#262626",
                            border: "1px solid #DBDBDB",
                            radius: "8px",
                          },
                          pinterest: {
                            bg: "#E60023",
                            color: "#FFFFFF",
                            radius: "24px",
                            shadow: "0 1px 4px rgba(0,0,0,0.15)",
                          },
                          linkedin: {
                            bg: "#0A66C2",
                            color: "#FFFFFF",
                            radius: "20px",
                          },
                          x: {
                            bg: "#000000",
                            color: "#FFFFFF",
                            radius: "9999px",
                          },
                        };
                        const brand: {
                          bg?: string;
                          color?: string;
                          border?: string;
                          radius?: string;
                          shadow?: string;
                        } = brandDefaults[subMasterName] ?? {};
                        const bs = aggregated?.buttonSettings;
                        const bgColor =
                          bs?.backgroundColor ?? brand.bg ?? "#EA33C0";
                        const textColor =
                          bs?.textColor ?? brand.color ?? "#ffffff";
                        const radius =
                          bs?.borderRadius != null
                            ? typeof bs.borderRadius === "number"
                              ? `${bs.borderRadius}px`
                              : bs.borderRadius
                            : (brand.radius ?? "8px");
                        // Saved settings win over the brand default so a custom
                        // border/shadow chosen in the builder survives.
                        const border = bs?.borderColor
                          ? `1px solid ${bs.borderColor}`
                          : (brand.border ?? "none");
                        const shadow = bs?.shadow ?? brand.shadow ?? "none";

                        return (
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: bgColor,
                              color: textColor,
                              fontSize: bs?.fontSize ?? 14,
                              fontWeight: bs?.fontWeight ?? "600",
                              borderRadius: radius,
                              border,
                              boxShadow: shadow,
                              cursor: "default",
                              letterSpacing: "0.3px",
                            }}
                          >
                            {bs?.text ??
                              aggregated?.label ??
                              comp.canvas_component_sub_master_display_name}
                          </div>
                        );
                      })()
                    ) : masterName === "preview" ? (
                      (() => {
                        const isPdf = subMasterName === "pdf_preview";
                        const docUrl =
                          mediaOverride?.url ??
                          mediaSlots.find(
                            (s) => s.canvas_component_info_id === comp.id,
                          )?.url ??
                          null;

                        if (docUrl && isPdf) {
                          return (
                            <iframe
                              src={docUrl}
                              style={{
                                width: "100%",
                                height: "100%",
                                border: "none",
                              }}
                              title="PDF Preview"
                            />
                          );
                        }

                        if (docUrl) {
                          return (
                            <div
                              style={{ width: "100%", height: "100%" }}
                              className="bg-blue-50 flex flex-col items-center justify-center gap-1"
                            >
                              <FileText size={24} className="text-blue-500" />
                              <span className="text-[9px] text-blue-600 font-medium text-center px-1 truncate w-full">
                                {mediaSlots.find(
                                  (s) =>
                                    s.canvas_component_info_id === comp.id,
                                )?.original_name ?? "Document"}
                              </span>
                            </div>
                          );
                        }

                        return (
                          <div
                            style={{ width: "100%", height: "100%" }}
                            className="bg-amber-50 border-2 border-dashed border-amber-200 flex flex-col items-center justify-center gap-1"
                          >
                            <FileText size={20} className="text-amber-400" />
                            <span className="text-[9px] font-medium text-amber-400">
                              {isPdf ? "PDF Preview" : "Document"}
                            </span>
                            <span className="text-[8px] text-amber-300">
                              Upload →
                            </span>
                          </div>
                        );
                      })()
                    ) : masterName === "icon" ? (
                      // Geometry and styling both come from the saved element,
                      // so the preview shows exactly the glyph the admin placed.
                      <ChromeIcon
                        geometry={aggregated?.iconGeometry}
                        settings={aggregated?.iconSettings}
                        label={
                          aggregated?.label ??
                          comp.canvas_component_sub_master_display_name
                        }
                      />
                    ) : masterName === "avatar" ? (
                      <ChromeAvatar
                        settings={aggregated?.avatarSettings}
                        url={mediaUrl}
                        label={
                          aggregated?.label ??
                          comp.canvas_component_sub_master_display_name
                        }
                      />
                    ) : masterName === "gradient" ? (
                      <ChromeGradient settings={aggregated?.gradientSettings} />
                    ) : masterName === "attributes" ? (
                      subMasterName === "safe_area" ? (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            backgroundColor: "rgba(234,51,192,0.08)",
                            border: "1.5px dashed #EA33C0",
                            pointerEvents: "none",
                          }}
                          className="flex items-center justify-center"
                        >
                          <span
                            style={{ color: "#EA33C0" }}
                            className="text-[9px] font-semibold tracking-wide"
                          >
                            SAFE AREA
                          </span>
                        </div>
                      ) : (
                        <div
                          style={{ width: "100%", height: "100%" }}
                          className="bg-blue-50 border-2 border-dashed border-blue-200 flex flex-col items-center justify-center gap-1"
                        >
                          <svg
                            className="w-5 h-5 text-blue-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span className="text-[9px] font-medium text-blue-400">
                            {aggregated?.label ??
                              comp.canvas_component_sub_master_display_name}
                          </span>
                        </div>
                      )
                    ) : (
                      <div
                        style={{ width: "100%", height: "100%" }}
                        className="bg-gray-50 border-2 border-dashed border-gray-200 flex items-center justify-center"
                      >
                        <span className="text-xs text-gray-400">
                          {aggregated?.label ??
                            comp.canvas_component_sub_master_display_name}
                        </span>
                      </div>
                    )}
                  </div>
                );
                };

                return (
                  <>
                    {/* The screen scrolls when expanded text makes it longer. */}
                    <div className="absolute inset-0 overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                      <div className="relative w-full" style={{ height: canvasHeight + totalExtra }}>
                        {visibleComponents.filter((c) => !isPinned(c)).map(renderComponent)}
                      </div>
                    </div>
                    {visibleComponents.filter(isPinned).map(renderComponent)}
                  </>
                );
              })()}
            </div>

            {/* Below the comment pins (z 49) so they stay clickable on the bezel. */}
            {isMobile && (
              <PhoneFrame
                width={canvasWidth}
                height={canvasHeight}
                scale={scale}
                className="z-[40]"
              />
            )}

            {/* Comment pins — positioned in scaled canvas space */}
            {canvasComments.map((comment, idx) => (
              <CanvasCommentPin
                key={comment.id}
                x={parseFloat(comment.x_position)}
                y={parseFloat(comment.y_position)}
                scale={scale}
                index={idx + 1}
                isResolved={comment.is_resolved}
                repliesCount={comment.replies_count}
                isActive={activeCommentId === comment.id}
                onClick={() => openDrawer(comment.id)}
              />
            ))}

            {/* Pending pin (placed but not yet submitted) */}
            {pendingPin && commentModeActive && (
              <div
                style={{
                  position: "absolute",
                  left: pendingPin.x * scale,
                  top: pendingPin.y * scale,
                  transform: "translate(-50%, -50%)",
                  zIndex: 9999,
                  pointerEvents: "none",
                }}
              >
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: "50% 50% 50% 0%",
                    backgroundColor: "#EA33C0",
                    border: "2.5px solid white",
                    boxShadow: "0 2px 8px rgba(234,51,192,0.5)",
                  }}
                />
              </div>
            )}

            {/* Click-to-comment hint */}
            {commentModeActive && !pendingPin && (
              <div
                style={{
                  position: "absolute",
                  bottom: 8,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 100,
                  pointerEvents: "none",
                }}
              >
                <div className="bg-black/70 text-white text-xs px-3 py-1.5 rounded-full whitespace-nowrap backdrop-blur-sm">
                  Click anywhere to add a comment
                </div>
              </div>
            )}
          </div>
        );

        const wrappedCanvas = isMobile ? (
          // Space for the phone hardware drawn outside the screen edge.
          <div
            className="mx-auto"
            style={{
              width: renderWidth + 2 * PHONE_FRAME_REACH * scale,
              padding: PHONE_FRAME_REACH * scale,
            }}
          >
            {canvasContent}
          </div>
        ) : (
          <div className="mx-auto border border-gray-200 shadow-md rounded-lg overflow-hidden" style={{ width: renderWidth }}>
            {canvasContent}
          </div>
        );

        if (showFullScreen) {
          return (
            <>
              {/* Fullscreen overlay */}
              <div
                className="fixed inset-0 z-50 flex flex-col"
                style={{ backgroundColor: "rgba(0,0,0,0.85)" }}
              >
                {/* Toolbar */}
                <div className="flex items-center justify-between px-6 py-3 bg-black/60 backdrop-blur-sm border-b border-white/10">
                  <div className="flex items-center gap-3">
                    {/* Device tabs */}
                    {Array.from(deviceSet).map((device) => (
                      <button
                        key={device}
                        onClick={() => setActiveDevice(device)}
                        className="px-4 py-1.5 rounded-full text-sm font-medium transition-colors capitalize"
                        style={
                          activeDevice === device
                            ? {
                                backgroundColor: "#EA33C0",
                                color: "#fff",
                              }
                            : {
                                backgroundColor: "rgba(255,255,255,0.1)",
                                color: "rgba(255,255,255,0.7)",
                              }
                        }
                      >
                        {device}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setShowFullScreen(false)}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
                  >
                    <Minimize2 size={15} />
                    Exit Full Screen
                  </button>
                </div>

                {/* Canvas centred */}
                <div className="flex-1 flex items-center justify-center overflow-auto p-8">
                  {wrappedCanvas}
                </div>
              </div>

              {/* Keep inline canvas hidden so layout doesn't shift */}
              <div className="invisible pointer-events-none">{wrappedCanvas}</div>
            </>
          );
        }

        return wrappedCanvas;
      })()}

      {/* Canvas Comments Drawer */}
      {!readOnly && isDrawerOpen && activeVersionId && (
        <CanvasCommentsDrawer
          versionId={activeVersionId}
          interfaceId={activeInterfaceIdRef.current}
          pendingPin={pendingPin}
          onClose={closeDrawer}
        />
      )}
    </div>
  );
};

export default AdBuilderCanvas;
